import { useEffect, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes, $getSelection, $isRangeSelection, $createParagraphNode } from 'lexical';
import { supabase } from '../../../../../lib/supabase';
import { $createImageNode } from './ImageNode';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ImagePlugin = ({ open, onClose }: Props) => {
  const [editor] = useLexicalComposerContext();
  const [tab, setTab] = useState<'upload' | 'url'>('upload');
  const [url, setUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setUrl('');
      setAltText('');
      setPreview('');
      setError('');
      setTab('upload');
    }
  }, [open]);

  const insertImage = (src: string) => {
    editor.update(() => {
      const selection = $getSelection();
      const imageNode = $createImageNode(src, altText || 'imagem');
      const paragraph = $createParagraphNode();
      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode, paragraph]);
      } else {
        $insertNodes([imageNode, paragraph]);
      }
    });
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Apenas arquivos de imagem são aceitos.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Tamanho máximo: 5 MB.'); return; }

    // local preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    setError('');
    try {
      const path = `broadcasts/${Date.now()}-${file.name.replace(/\s/g, '_')}`;

      // Try to create bucket if it doesn't exist (silently ignore if it does)
      await supabase.storage.createBucket('broadcasts', { public: true }).catch(() => {});

      const { error: uploadError } = await supabase.storage.from('broadcasts').upload(path, file, { upsert: true });
      if (uploadError) throw new Error(uploadError.message);

      const { data: { publicUrl } } = supabase.storage.from('broadcasts').getPublicUrl(path);
      setUrl(publicUrl);
      setPreview(publicUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer upload da imagem.');
      setPreview('');
    }
    setUploading(false);
  };

  const handleInsert = () => {
    const src = tab === 'url' ? url : preview;
    if (!src) { setError('Adicione uma imagem primeiro.'); return; }
    insertImage(src);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="bg-[#0F0F1A] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-700 text-white flex items-center gap-2">
            <i className="ri-image-add-line text-violet-400" />
            Inserir imagem no conteúdo
          </h3>
          <button type="button" onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {(['upload', 'url'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); setError(''); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
                tab === t ? 'bg-violet-600/30 text-violet-300' : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white'
              }`}
            >
              <i className={t === 'upload' ? 'ri-upload-2-line text-[12px]' : 'ri-link text-[12px]'} />
              {t === 'upload' ? 'Upload' : 'URL externa'}
            </button>
          ))}
        </div>

        {tab === 'upload' && (
          <div
            className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:border-violet-500/40 hover:bg-white/5 transition-all"
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
                <span className="text-white/40 text-[13px]">Enviando...</span>
              </div>
            ) : preview ? (
              <img src={preview} alt="preview" className="max-h-32 mx-auto rounded-lg object-contain" />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <i className="ri-image-add-line text-3xl text-white/20" />
                <p className="text-[13px] text-white/40">Clique para selecionar uma imagem</p>
                <p className="text-[11px] text-white/20">PNG, JPG, GIF — máx. 5 MB</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
          </div>
        )}

        {tab === 'url' && (
          <input
            type="url"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setPreview(e.target.value); }}
            placeholder="https://exemplo.com/imagem.jpg"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-[13px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
          />
        )}

        {tab === 'url' && preview && (
          <div className="mt-3 rounded-xl overflow-hidden border border-white/10">
            <img src={preview} alt="preview" className="w-full max-h-32 object-contain" />
          </div>
        )}

        <div className="mt-3">
          <input
            type="text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="Texto alternativo (opcional)"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-[12px] placeholder-white/20 focus:outline-none focus:border-violet-500/60 transition-colors"
          />
        </div>

        {error && <p className="text-red-400 text-[12px] mt-2 flex items-center gap-1"><i className="ri-error-warning-line" />{error}</p>}

        <div className="flex gap-3 mt-4">
          <button
            type="button"
            onClick={handleInsert}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 rounded-xl text-white text-[13px] font-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-image-add-line text-[14px]" />
            Inserir imagem
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/50 hover:text-white text-[13px] cursor-pointer transition-colors whitespace-nowrap">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};
